import { API_KEY } from './apiConstants';
import { getGATHeaders } from '../features/gat/gatSlice';
import { store } from '../app/store';

export async function api<T>(
  url: string,
  value: string,
  controller: AbortController,
): Promise<T | Failure> {
  return fetch(`${url}${value}`, { signal: controller.signal }).then((response) => {
    if (!response.ok) {
      return new Failure(response.status, response.statusText);
    }
    return response.json() as Promise<T | Failure>;
  });
}

export async function apiGetV2<T>(
  url: string,
  query: string,
  controller?: AbortController,
  customHeaders?: Record<string, string>,
): Promise<T | Failure> {
  const fullUrl = query ? `${url}?${query}` : url;
  const gatHeaders = getGATHeaders(store.getState());

  const response = await fetch(fullUrl, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...headers(gatHeaders),
      ...customHeaders,
    },
    signal: controller?.signal,
  });

  if (!response.ok) {
    return new Failure(response.status, response.statusText);
  }

  return response.json() as Promise<T>;
}

export async function apiPostV2<T>(
  url: string,
  body: any,
  controller?: AbortController,
  customHeaders?: Record<string, string>,
): Promise<T | Failure> {
  const gatHeaders = getGATHeaders(store.getState());

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      ...headers(gatHeaders),
      ...customHeaders, // om du t.ex. vill överstyra något manuellt
    },
    body: JSON.stringify(body),
    signal: controller?.signal,
  });

  if (!response.ok) {
    return new Failure(response.status, response.statusText);
  }

  return response.json() as Promise<T>;
}

export async function apiPost<T>(
  url: string,
  query: string,
  controller: AbortController,
): Promise<T | Failure> {
  return fetch(`${url}?${query}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    signal: controller.signal,
  }).then((response) => {
    if (!response.ok) {
      return new Failure(response.status, response.statusText);
    }
    return response.json() as Promise<T | Failure>;
  });
}

export async function newApiPost<T>(
  url: string,
  x: { organisationNumber: string; mobileNumber: string; email: string },
): Promise<T | NewFailure> {
  return fetch(`${url}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(x),
  })
    .then((response) => {
      return response.json();
    })
    .then((response) => {
      // eslint-disable-next-line no-console

      if (!response.ok && response.status !== 'Active') {
        const status = response.StatusCode !== undefined ? response.StatusCode : response.status;
        const message = response.Message ? response.Message : response.message;
        const code = response.Code ? response.Code : response.code;

        return new NewFailure(status, message, code);
      }

      return response as Promise<T | NewFailure>;
    });
}

export class NewFailure {
  StatusCode: number;
  Message: string;
  Code: string;
  constructor(status: number, message: string, code: string) {
    this.StatusCode = status;
    this.Message = message;
    this.Code = code;
  }
}

export class Failure {
  status: number;
  statusText: string;
  constructor(status: number, statusText: string) {
    this.status = status;
    this.statusText = statusText;
  }
}

export async function apiDelete<T>(
  url: string,
  query: string,
  controller: AbortController,
): Promise<void | Failure> {
  return fetch(`${url}/${query}`, {
    method: 'Delete',
    headers: { 'Content-Type': 'application/json' },
    signal: controller.signal,
  }).then((response) => {
    if (!response.ok) {
      return new Failure(response.status, response.statusText);
    }
    return response.json() as Promise<void | Failure>;
  });
}

export async function getGeocodeGoogle<T>(url: string, params: string): Promise<T | Failure> {
  return fetch(`${url}${params}${API_KEY}`).then((response) => {
    if (!response.ok) {
      throw new Failure(response.status, response.statusText);
    }
    return response.json() as Promise<T>;
  });
}

export async function getPlacesGoogle<T>(url: string, params: string): Promise<T> {
  return fetch(`${url}${params}&types=address${API_KEY}`).then((response) => {
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    return response.json() as Promise<T>;
  });
}

export const GAT_DEV = 'https://gat.dev.assi.nu/';
export const GAT_STG = 'https://gat.stg.assi.nu/';
export const GAT_PROD = 'https://api.smashtechsoftware.com/';

export function getBaseUrl() {
  if (process.env.REACT_APP_ENV === 'PRODUCTION') {
    return GAT_PROD;
  }
  if (process.env.REACT_APP_ENV === 'STAGING') {
    return GAT_STG;
  }
  return GAT_DEV;
}

function headers(gatHeaders: ReturnType<typeof getGATHeaders>) {
  const { gatLocale, gatOrgUnit, appId, accessToken } = gatHeaders;

  const result: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (accessToken) {
    result.Authorization = `Bearer ${accessToken}`;
  }
  if (gatLocale) {
    result['gat-locale'] = gatLocale;
  }
  if (gatOrgUnit) {
    result['gat-organization-unit'] = gatOrgUnit;
  }
  if (appId) {
    result['gat-application-id'] = appId;
  }

  return result;
}
